import {
  getContentBoxTemplate,
  CONTENT_BOX_FULL_WIDTH_CLASSES,
  getContentBoxFullWidthClass,
  getContentBoxStyle,
  CONTENT_BOX_MOBILE_CLASS,
  CONTENT_BOX_DATA_ATTRIBUTE,
  encodeContentBoxData,
  decodeContentBoxData,
  getContentBoxRowsTemplate,
} from "../../shortcodes/templates";
import { addOnClickDivs } from "../../shortodeAddition";
import { openUrlWithoutFullscreenToggle } from "../../helpers/dialogFullscreen";
import { parseToHtml } from "@/utility/parseToHtml";
import {
  CONTENT_BOX_EVENT_TYPES,
  CONTENT_BOX_BORDER_STYLES,
  CONTENT_BOX_BORDER_SIDES,
  DEFAULT_CONTENT_BOX_BORDER_WIDTH,
  DEFAULT_CONTENT_BOX_BORDER_COLOR,
  DEFAULT_CONTENT_BOX_MARGIN,
  DEFAULT_CONTENT_BOX_MARGIN_BOX,
  DEFAULT_CONTENT_BOX_PADDING,
  DEFAULT_CONTENT_BOX_LINE_LENGTH,
} from "@/constant";

const DEFAULT_BACKGROUND_COLOR = "#EEF2FB";
const DEFAULT_MAX_WIDTH = 770;
const DEFAULT_CORNER_RADIUS = 0;

const DIALOG_VIEWPORT_INSET = 20;

const toPxNumber = (value, fallback) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

// Boxes added before per-side borders carry no inline widths at all, only the legacy
// class, so their top border has to be inferred from it.
const readBorder = box => {
  const widths = {
    top: parseInt(box.style.borderTopWidth, 10),
    right: parseInt(box.style.borderRightWidth, 10),
    bottom: parseInt(box.style.borderBottomWidth, 10),
    left: parseInt(box.style.borderLeftWidth, 10),
  };

  if (Object.values(widths).every(Number.isNaN)) {
    return {
      sides: { top: !box.classList.contains("content-box--no-border"), right: false, bottom: false, left: false },
      width: DEFAULT_CONTENT_BOX_BORDER_WIDTH,
    };
  }

  return {
    sides: { top: widths.top > 0, right: widths.right > 0, bottom: widths.bottom > 0, left: widths.left > 0 },
    width: Object.values(widths).find(width => width > 0) || DEFAULT_CONTENT_BOX_BORDER_WIDTH,
  };
};

// The editor DOM serializes inline colors to rgb(), the color picker expects hex.
const toHexColor = value => {
  const match = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(value || "");
  if (!match) return value || "";
  return `#${match
    .slice(1)
    .map(part => Number(part).toString(16).padStart(2, "0"))
    .join("")}`;
};

const stripEditorArtifacts = html => {
  const parsed = parseToHtml(html);
  parsed.querySelectorAll("[data-shortcode-remove-onclick]").forEach(element => element.remove());
  return parsed.body.innerHTML;
};

const sanitizeData = data => ({
  ...data,
  rows: data.rows.map(row => ({
    columns: row.columns.map(column => ({
      content: stripEditorArtifacts(column.content),
      width: column.width ?? null,
    })),
  })),
});

const stripRemoveButtons = root => {
  root.querySelectorAll("[data-shortcode-remove-onclick]").forEach(element => element.remove());
  return root;
};

const readColumnContent = column => ({
  content: stripRemoveButtons(column.cloneNode(true)).innerHTML,
  width: Number.parseFloat(column.style?.getPropertyValue("--column-width")) || null,
});

const isMeaningfulStray = node => {
  if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent.trim());
  if (node.nodeType !== Node.ELEMENT_NODE || node.nodeName === "BR" || node.hasAttribute("data-mce-bogus")) {
    return false;
  }
  return (
    Boolean(node.textContent.trim()) ||
    node.matches("img, video, iframe") ||
    Boolean(node.querySelector("img, video, iframe"))
  );
};

const strayContent = node =>
  node.nodeType === Node.TEXT_NODE ? node.textContent : stripRemoveButtons(node.cloneNode(true)).outerHTML;

// Content can end up directly at box or row level (caret between rows, TinyMCE moving
// blocks around), and the dialog rebuilds the box from its data on save, so anything
// not read here would be silently dropped. Every meaningful stray child is therefore
// preserved in document order as its own column / single-column row. Walking direct
// children only also keeps a content box nested inside a column from leaking its rows
// into the outer box's data.
const readRowColumns = row => {
  const columns = [];
  let stray = null;
  for (const node of [...row.childNodes]) {
    if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains("content-box__column")) {
      stray = null;
      columns.push(readColumnContent(node));
    } else if (isMeaningfulStray(node)) {
      if (!stray) {
        stray = { content: "" };
        columns.push(stray);
      }
      stray.content += strayContent(node);
    }
  }
  return columns.length ? columns : [readColumnContent(row)];
};

const readBoxRows = box => {
  const rows = [];
  let stray = null;
  for (const node of [...box.childNodes]) {
    if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains("content-box__columns")) {
      stray = null;
      rows.push({ columns: readRowColumns(node) });
    } else if (isMeaningfulStray(node)) {
      if (!stray) {
        stray = { content: "" };
        rows.push({ columns: [stray] });
      }
      stray.content += strayContent(node);
    }
  }
  return rows.length ? rows : [{ columns: [readColumnContent(box)] }];
};

// Legacy boxes zeroed both margins with the `--no-margin` class.
const readMargin = (inlineValue, box) => {
  const parsed = Number.parseFloat(inlineValue);
  if (Number.isFinite(parsed)) return parsed;
  return box.classList.contains("content-box--no-margin") ? 0 : DEFAULT_CONTENT_BOX_MARGIN;
};

const readFullWidth = box => {
  const both = box.classList.contains(CONTENT_BOX_FULL_WIDTH_CLASSES.both);
  const fullWidth = both || box.classList.contains(CONTENT_BOX_FULL_WIDTH_CLASSES.desktop);
  const onMobile = both || box.classList.contains(CONTENT_BOX_FULL_WIDTH_CLASSES.mobile);

  return { fullWidth, separateMobile: fullWidth !== onMobile, mobile: { fullWidth: onMobile } };
};

// Fallback for boxes saved before the config attribute.
const readBoxStyles = box => {
  const maxWidth = parseInt(box.querySelector(":scope > .content-box__columns")?.style.maxWidth, 10);
  const border = readBorder(box);

  return {
    borderSides: border.sides,
    borderColor: toHexColor(box.style.borderColor || box.style.borderTopColor) || DEFAULT_CONTENT_BOX_BORDER_COLOR,
    borderWidth: border.width,
    cornerRadius: Math.max(
      0,
      toPxNumber(box.style.borderTopLeftRadius, DEFAULT_CORNER_RADIUS) -
        (border.sides.top || border.sides.left ? border.width : 0),
    ),
    enableBackground: !box.classList.contains("content-box--no-background"),
    backgroundColor: toHexColor(box.style.backgroundColor) || DEFAULT_BACKGROUND_COLOR,
    ...readFullWidth(box),
    margin: readMargin(box.style.marginTop, box),
    enableMaxWidth: !Number.isNaN(maxWidth),
    maxWidth: !Number.isNaN(maxWidth) ? maxWidth : DEFAULT_MAX_WIDTH,
  };
};

// `fullWidth` was a mode string before it became a flag per viewport.
const normalizeFullWidth = config => {
  if ("string" !== typeof config.fullWidth) return config;

  return {
    ...config,
    fullWidth: ["desktop", "both"].includes(config.fullWidth),
    separateMobile: config.separateMobile || ["desktop", "mobile"].includes(config.fullWidth),
    mobile: { ...config.mobile, fullWidth: ["mobile", "both"].includes(config.fullWidth) },
  };
};

const toLength = (value, fallback) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const toLineLength = value => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100 ? parsed : DEFAULT_CONTENT_BOX_LINE_LENGTH;
};

const toSides = (value, fallback) =>
  CONTENT_BOX_BORDER_SIDES.reduce((sides, side) => ({ ...sides, [side]: toLength(value?.[side], fallback[side]) }), {});

// Boxes saved before per-side margins carry one vertical value.
const toMarginBox = value => {
  if (value && "object" === typeof value) return toSides(value, DEFAULT_CONTENT_BOX_MARGIN_BOX);

  const vertical = toLength(value, DEFAULT_CONTENT_BOX_MARGIN);
  return { ...DEFAULT_CONTENT_BOX_MARGIN_BOX, top: vertical, bottom: vertical };
};

// Boxes saved before per-side widths carry `borderSides` flags and one shared width.
const toBorder = ({ borderSides = { top: true }, borderStyle, borderWidth, borderColor }) => {
  const sharedWidth =
    "object" === typeof borderWidth
      ? DEFAULT_CONTENT_BOX_BORDER_WIDTH
      : toLength(borderWidth, DEFAULT_CONTENT_BOX_BORDER_WIDTH);
  const legacyWidth = CONTENT_BOX_BORDER_SIDES.reduce(
    (sides, side) => ({ ...sides, [side]: borderSides[side] ? sharedWidth : 0 }),
    {},
  );

  return {
    borderStyle: CONTENT_BOX_BORDER_STYLES.includes(borderStyle) ? borderStyle : "solid",
    borderColor: borderColor || DEFAULT_CONTENT_BOX_BORDER_COLOR,
    borderWidth: toSides(borderWidth, legacyWidth),
  };
};

const normalizeStyles = ({
  borderSides,
  borderStyle,
  borderWidth,
  borderColor,
  cornerRadius,
  enableBackground = true,
  backgroundColor,
  margin,
  padding,
  lineLength,
  ...rest
}) => ({
  ...rest,
  ...toBorder({ borderSides, borderStyle, borderWidth, borderColor }),
  cornerRadius: toLength(cornerRadius, DEFAULT_CORNER_RADIUS),
  enableBackground,
  backgroundColor: backgroundColor || DEFAULT_BACKGROUND_COLOR,
  margin: toMarginBox(margin),
  padding: toSides(padding, DEFAULT_CONTENT_BOX_PADDING),
  lineLength: toLineLength(lineLength),
});

// Mobile inherits whatever it does not override, so a box saved before a mobile option
// existed keeps rendering the desktop value there.
const normalizeData = data => {
  const { mobile = {}, ...desktop } = normalizeFullWidth(data);

  return {
    ...normalizeStyles(desktop),
    mobile: normalizeStyles({
      ...mobile,
      borderSides: mobile.borderSides ?? desktop.borderSides,
      borderStyle: mobile.borderStyle ?? desktop.borderStyle,
      borderWidth: mobile.borderWidth ?? desktop.borderWidth,
      borderColor: mobile.borderColor ?? desktop.borderColor,
      cornerRadius: mobile.cornerRadius ?? desktop.cornerRadius,
      enableBackground: mobile.enableBackground ?? desktop.enableBackground,
      backgroundColor: mobile.backgroundColor ?? desktop.backgroundColor,
      margin: mobile.margin ?? desktop.margin,
      padding: mobile.padding ?? desktop.padding,
      lineLength: mobile.lineLength ?? desktop.lineLength,
    }),
  };
};

const readBoxData = box => {
  const stored = box.getAttribute(CONTENT_BOX_DATA_ATTRIBUTE);

  return normalizeData({
    ...(stored ? decodeContentBoxData(stored) : readBoxStyles(box)),
    rows: readBoxRows(box),
  });
};

const openContentBoxDialog = ({ editor, submitText, initialData, onValid }) => {
  let currentData = null;
  let isValid = null;

  const contentBoxWindow = openUrlWithoutFullscreenToggle(editor, {
    title: "Content Box",
    url: "/content-box-dialog",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: submitText,
        primary: true,
      },
    ],
    onAction: function (instance) {
      if (!isValid || !currentData) {
        contentBoxWindow.sendMessage({
          type: CONTENT_BOX_EVENT_TYPES.NEED_VALIDATION,
          data: true,
        });

        return;
      }

      onValid(sanitizeData(currentData));
      instance.close();
    },
    onMessage: (instance, message) => {
      if (message.mceAction === CONTENT_BOX_EVENT_TYPES.REQUEST_DATA) {
        contentBoxWindow.sendMessage({
          type: CONTENT_BOX_EVENT_TYPES.SEND_DATA,
          data: initialData || {},
        });

        return;
      }

      if (message.mceAction === CONTENT_BOX_EVENT_TYPES.UPDATE_CONTENT_BOX_DATA && message.data) {
        currentData = message.data;
        isValid = true;

        return;
      }

      if (message.mceAction === CONTENT_BOX_EVENT_TYPES.VALIDATION) {
        isValid = message.data.isValid;
      }
    },
    width: window.innerWidth - DIALOG_VIEWPORT_INSET,
    height: window.innerHeight - DIALOG_VIEWPORT_INSET,
  });
};

/**
 * The content box is a container element (like columns): it is inserted without the
 * `.shortcode` wrapper so other design elements can live inside its columns.
 */
const contentBoxConfig = tinymce => {
  const editor = tinymce.activeEditor;

  openContentBoxDialog({
    editor,
    submitText: "Add",
    initialData: null,
    onValid: data => {
      editor.execCommand("mceInsertContent", false, getContentBoxTemplate(data) + "<p></p>");
      addOnClickDivs(editor);
      editor.fire("change");
    },
  });
};

/**
 * Only the box options and column contents are owned by the dialog; the box element
 * itself is mutated in place so undo history and surrounding content survive.
 */
const editContentBoxConfig = (editor, box) => {
  openContentBoxDialog({
    editor,
    submitText: "Save",
    initialData: readBoxData(box),
    onValid: data => {
      // setAttrib, because `dom.setStyles` cannot set custom properties.
      editor.dom.setAttrib(box, "style", getContentBoxStyle(data));
      editor.dom.toggleClass(box, CONTENT_BOX_MOBILE_CLASS, Boolean(data.separateMobile));
      editor.dom.toggleClass(box, "content-box--no-border", false);
      editor.dom.toggleClass(box, "content-box--no-background", false);
      editor.dom.toggleClass(box, "content-box--no-margin", false);
      const fullWidthClass = getContentBoxFullWidthClass(data);
      Object.values(CONTENT_BOX_FULL_WIDTH_CLASSES).forEach(className =>
        editor.dom.toggleClass(box, className, className === fullWidthClass),
      );
      box.setAttribute(CONTENT_BOX_DATA_ATTRIBUTE, encodeContentBoxData(data));
      box.innerHTML = getContentBoxRowsTemplate(data);
      addOnClickDivs(editor);
      editor.fire("change");
    },
  });
};

export { contentBoxConfig, editContentBoxConfig };
